import RegulationRevisionDates from "@/components/atoms/RegulationRevisionDates";
import { KPMGDataProvisionUsagePurpose } from "@/components/atoms/terms/bank/kpmg/kpmg";
import {
  DataUsagePurposeBase,
  DataUsagePurposeInsuranceAgency,
  DataUsagePurposeJPBank,
} from "@/components/atoms/terms/DataProvision";
import { MainTemplate } from "@/components/templates";
import { useIsSpMode } from "@/hooks";
import config from "@/libs/config";
import { BankCode } from "@/libs/constants";
import { css } from "@emotion/react";

type Props = {
  bankCode: BankCode;
  bankFormalName: string;
  dataProvisionRegulationDate: string;
  dataProvisionRevisionDate: string;
  additionalProviders?: string[];
  isInsuranceAgency?: boolean;
};

export const TermsOfPolicyDataProvisionPage = ({
  bankCode,
  bankFormalName,
  dataProvisionRegulationDate,
  dataProvisionRevisionDate,
  additionalProviders = [],
  isInsuranceAgency = false,
}: Props) => {
  const isSpMode = useIsSpMode();

  const DataUsagePurposeBody = () => {
    switch (true) {
      case bankCode == BankCode.JPBank:
        return DataUsagePurposeJPBank();
      case bankCode == BankCode.KPMG:
        return KPMGDataProvisionUsagePurpose();
      case isInsuranceAgency:
        return DataUsagePurposeInsuranceAgency();
    }
    return DataUsagePurposeBase();
  };

  return (
    <MainTemplate term={true} title={"利用者情報の第三者提供に関する特約"}>
      <h2 css={!isSpMode ? title : spModeTitle}>
        利用者情報の第三者提供に関する特約
      </h2>
      <div css={!isSpMode ? container : spModeContainer}>
        <div css={content}>
          「利用者情報の第三者提供に関する特約」（以下「本特約」といいます。）は、マネーフォワードエックス株式会社（以下「当社」といいます。）が提供するMikatano
          IDサービス及び「指定サービスに関する特約」に定める指定サービスの利用者に適用されます。本特約については、利用希望者（Mikatano
          IDサービス又は指定サービスを利用するに際し、当社とMikatano
          IDサービス又は指定サービスの利用に関する契約を締結することを希望する法人、団体又は個人事業主をいいます。以下同じです。）が利用登録した時点で同意したものとさせていただきますので、ご利用の前に必ずお読みください。
          <br />
          なお、本特約の中で使用される用語については、本特約中に別段の定めのある場合を除き、
          <a href="/terms/id" target="_blank" css={a}>
            Mikatano ID利用規約
          </a>
          及び指定サービス利用規約の中で使用される定義が適用されるものとします。
        </div>
        <h4 css={articleTitle}>第１条（利用者情報の第三者提供）</h4>
        <div css={content}>
          契約者は、当社がMikatano
          IDサービス及び指定サービスに関し取得する利用者に関する情報を、次のとおり第三者に随時提供することについて、同意するものとします。
        </div>
        <ol css={ol}>
          <li>
            情報提供先である第三者
            <br />
            {bankFormalName}
            {additionalProviders &&
              additionalProviders.map((provider, index) => (
                <>
                  <br />
                  {provider}
                </>
              ))}
          </li>
          <li>
            利用目的
            <br />
            <DataUsagePurposeBody />
          </li>
          <li>
            提供される利用者に関する情報の内容
            <br />
            利用者が、Mikatano
            IDサービス及び指定サービスにおいて当社に提供し、又は利用者の提供した情報に基づき当社がアクセスできる一切の利用者に関する情報（次に掲げる情報を含みます。）。ただし、第三者に提供する情報に当社がアクセスするために必要な認証情報として利用者が登録した情報（ユーザーID及びパスワード等）自体は含みません。
            <br />
            {!isInsuranceAgency && (
              <>
                ・契約者の口座情報
                <br />
              </>
            )}
            ・利用者の氏名及び所属先部署の名称
            <br />
            ・利用者のメールアドレス
            <br />
            ・代表者の氏名
            {isInsuranceAgency && (
              <>
                <br />
                ・指定サービスへのアクセスログ
                <br />
                ・指定サービス上で、利用者が選択・入力・アップロードした情報
              </>
            )}
          </li>
          <li>
            第三者への提供の停止方法
            <br />
            利用者が本条に定める情報の第三者への提供を停止することを希望する場合、契約者は当社所定の手続きを完了することにより当該提供を停止することができます。この場合、当社は、当該契約者がMikatano
            ID利用規約第24条に定める方法によりMikatano
            IDサービス及び全ての指定サービスから退会したものとみなします。
          </li>
        </ol>
        <h4 css={articleTitle}> 第2条（準拠法及び管轄）</h4>
        <div css={content}>
          本特約の準拠法は日本法とし、本特約に起因し又は関連する一切の紛争については、東京地方裁判所を第一審の専属的合意管轄裁判所とします。
        </div>
        <h4 css={articleTitle}> 第3条（協議解決）</h4>
        <div css={content}>
          当社、利用者及び利用希望者は、本特約に定めのない事項又は本特約の解釈に疑義が生じた場合には、互いに信義誠実の原則に従って協議の上速やかに解決を図るものとします。
        </div>
        <div css={content}>
          <RegulationRevisionDates
            regulationDate={dataProvisionRegulationDate}
            revisionDate={dataProvisionRevisionDate}
          />
        </div>
      </div>
    </MainTemplate>
  );
};

const title = css({
  margin: "auto",
  width: "703px",
  fontWeight: "bold",
  marginBottom: "20px",
});

const spModeTitle = css({
  margin: "auto",
  fontWeight: "bold",
  marginBottom: "20px",
});

const container = css({
  boxSizing: "border-box",
  margin: "auto",
  alignItems: "flexStart",
  padding: "3px 0px 36px",
  width: "703px",
  border: "1px solid #DADEDF",
  borderRadius: "2px",
});

const spModeContainer = css({
  boxSizing: "border-box",
  margin: "auto",
  alignItems: "flexStart",
  padding: "3px 0px 36px",
  border: "1px solid #DADEDF",
  borderRadius: "2px",
});

const content = css({
  margin: "0 auto",
  padding: "14px 24px 0px",
  marginBottom: "20px",
});

const articleTitle = css({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  padding: "24px 0px 16px 24px",
  gap: "10px",
  width: "100%",
  height: "54px",
  fontWeight: "900",
});

const ol = css`
  li {
    list-style-type: none;
    counter-increment: cnt;
    margin-left: 1.4em;
    text-indent: -3em;
    margin-bottom: 15px;
    padding-right: 24px;
  }
  li::before {
    content: "（" counter(cnt) "）";
  }
`;

const a = css({
  color: "#1A73E8",
});
